import { createColumnHelper, type ColumnDef } from "@tanstack/react-table";
import {
  Avatar,
  AvatarFallback,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@elaniintc/goes-design-system";

import type { FormalityRequestType } from "@/types/entities/formality-request";
import {
  formatDateTime,
  getFormattedElapsedDaysWithColor,
} from "@/utils/dates";
import { TableLeadingCell } from "@/components/ui/table-leading-cell";
import RequestStatus from "./request-state";
import type { RefName } from "@/workflow_api/types/core";
import Menu from "./request-management-columns.comp";

const columnHelper = createColumnHelper<FormalityRequestType>();

const getUserInitial = (name?: string, lastName?: string) => {
  const fullName = `${name || ""} ${lastName || ""}`.trim();
  if (!fullName) return "?";
  return fullName.charAt(0).toUpperCase();
};

const getUniqueAssignedUsers = (groups: FormalityRequestType["groups"]) => {
  const assignedUsers = groups
    .map((group) => group?.pivot?.assigned_user)
    .filter((user): user is NonNullable<typeof user> => !!user);

  return assignedUsers.filter((user, index, users) => {
    const currentUserKey =
      user.id?.toString() ||
      user.email ||
      `${user.name || ""}-${user.last_name || ""}`;

    return (
      index ===
      users.findIndex((candidate) => {
        const candidateKey =
          candidate.id?.toString() ||
          candidate.email ||
          `${candidate.name || ""}-${candidate.last_name || ""}`;

        return candidateKey === currentUserKey;
      })
    );
  });
};

const MultipleAssignedTooltip = ({
  groups,
}: {
  groups: FormalityRequestType["groups"];
}) => {
  const assignedUsersList = getUniqueAssignedUsers(groups);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex items-center gap-4 cursor-pointer">
          <Avatar size="xs">
            <AvatarFallback color="action">
              <Icon
                type="fas"
                name="users"
                className="text-action-quiet"
                size="sm"
              />
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col gap-0">
            <span className="text-body font-normal">Múltiples asignados</span>
            <span className="text-body-quiet font-normal text-xs">
              {assignedUsersList.length} usuarios asignados
            </span>
          </div>
        </div>
      </TooltipTrigger>
      <TooltipContent side="top" align="start" className="max-w-70 p-3">
        <div className="scrollbar-thin-goes flex flex-col gap-2 max-h-72 overflow-y-auto pr-1">
          {assignedUsersList.length > 0 ? (
            assignedUsersList.map((user, index) => (
              <div
                key={`${user.name}-${user.last_name}-${index}`}
                className="flex items-center gap-2"
              >
                <Avatar size="xs">
                  <AvatarFallback color="action">
                    {getUserInitial(user.name, user.last_name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col">
                  <span className="text-body font-medium leading-5">
                    {`${user.name || ""} ${user.last_name || ""}`.trim()}
                  </span>
                  <span className="text-body-quiet text-xs leading-4">
                    {user.position?.nombre || user.position?.name || ""}
                  </span>
                  <span className="text-body-quiet text-xs leading-4 break-all">
                    {user.email || ""}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <span className="text-body-quiet text-xs">
              Sin usuarios asignados
            </span>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

export const createColumns = (
  hasActivityBlock: boolean,
  setModalState: (state: "idle" | "filters" | "userSelection") => void,
  onUpdateAssignee: (request: FormalityRequestType) => void,
  setGroupsIdToFind: (groupsId: string[]) => void,
  onNavigate: (id: string) => void,
  shouldShowAssignedColumn = false,
  groupsImLeaderOf: string[] = [],
) =>
  [
    columnHelper.accessor("process.name", {
      id: "process.name",
      header: "Nombre del trámite",
      sortUndefined: "last",
      meta: {
        className:
          "max-w-[80px] sm:max-w-[250px] text-ellipsis overflow-hidden",
        cellClassName: "group",
      },
      cell: (info) => {
        if (!info.getValue()) return <p>&nbsp;</p>;
        return (
          <TableLeadingCell
            title={info.getValue()}
            showButton={true}
            onButtonClick={() => {
              const id = info?.row?.original?.id;
              if (!id) return;
              onNavigate(id);
            }}
          />
        );
      },
    }),

    columnHelper.accessor("code", {
      header: "Número de solicitud",
      sortUndefined: "last",
      meta: {
        className:
          "hidden min-[1500px]:table-cell text-ellipsis overflow-hidden",
      },
      cell: (info) => {
        if (!info.getValue()) return <p>&nbsp;</p>;
        return info.getValue().toUpperCase();
      },
    }),

    columnHelper.accessor("status", {
      header: "Estado",
      sortUndefined: "last",
      meta: {
        className: "hidden min-[1400px]:table-cell",
      },
      cell: (info) => {
        return (
          <RequestStatus
            node_name={
              info.row.original.current_node?.display_name || "Desconocido"
            }
            node_type={
              (info.row.original.current_node?.ref_name as RefName) ||
              "resolution-node"
            }
            status={info.row.original.status || "cancelled"}
            status_label={info.row.original.status_label || ""}
            flow_status={info.row.original.flow_status}
          />
        );
      },
    }),

    columnHelper.accessor((row) => row?.assignedTo?.name, {
      header: "Asignado",
      id: "groups",
      sortUndefined: "last",
      enableSorting: false,
      meta: {
        className: `hidden ${hasActivityBlock ? "lg:table-cell" : " 3xl:table-cell"} `,
      },
      cell: (info) => {
        const groups = info.row.original.groups;
        const assignedTo = info.row.original.assignedTo;
        const uniqueAssignedUsers = getUniqueAssignedUsers(groups);

        if (groups.length === 0) {
          if (!assignedTo?.name) return <p>&nbsp;</p>;

          const initials = assignedTo.name.split(" ")[0][0];

          return (
            <div className="flex items-center gap-4">
              <Avatar size="xs">
                <AvatarFallback color="action">{initials}</AvatarFallback>
              </Avatar>
              <div className="flex flex-col gap-0">
                <span className="text-body font-normal">
                  {`${assignedTo?.name || ""} ${assignedTo?.last_name || ""}`}
                </span>
                <span className="text-body-quiet font-normal text-xs">
                  {assignedTo?.position?.name || ""}
                </span>
              </div>
            </div>
          );
        }

        if (groups.length === 1 || uniqueAssignedUsers.length === 1) {
          const singleUniqueAssignedUser = uniqueAssignedUsers[0];
          const groupAssignedUser =
            assignedTo ||
            singleUniqueAssignedUser ||
            groups[0].pivot.assigned_user;
          const initials = groupAssignedUser?.name.split(" ")[0][0];

          return (
            <div className="flex items-center gap-4">
              <Avatar size="xs">
                <AvatarFallback color="action">{initials}</AvatarFallback>
              </Avatar>
              <div className="flex flex-col gap-0">
                <span className="text-body font-normal">
                  {`${groupAssignedUser?.name || ""} ${groupAssignedUser?.last_name || ""}`}
                </span>
              </div>
            </div>
          );
        }

        return <MultipleAssignedTooltip groups={groups} />;
      },
    }),

    columnHelper.accessor("created_at", {
      id: "created_at",
      header: "Fecha de ingreso",
      meta: {
        className: `hidden ${hasActivityBlock ? "lg:table-cell" : "3xl:table-cell"} `,
      },
      sortUndefined: "last",
      cell: (info) => {
        return formatDateTime(info?.getValue() || "");
      },
    }),

    columnHelper.accessor("first_assigned_at", {
      id: "elapsed_days",
      header: () => (
        <div className="flex items-center justify-between flex-auto">
          <span>Tiempo transcurrido</span>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-2">
                <Avatar size="xs">
                  <AvatarFallback color="neutral">
                    <Icon
                      type="fas"
                      name="info-circle"
                      className="text-body-quiet"
                      size="xs"
                    />
                  </AvatarFallback>
                </Avatar>
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" align="center" className="w-62.5">
              <p>
                Calculado a partir del momento en el que la solicitud fue
                asignada
              </p>
            </TooltipContent>
          </Tooltip>
        </div>
      ),
      meta: {
        className: "hidden md:table-cell",
      },
      sortUndefined: "first",
      cell: (row) => {
        const creationDate = row.row?.original?.first_assigned_at;
        const elapsedTimeStatus = row.row?.original?.elapsed_time_status;
        const isUnassigned = elapsedTimeStatus === "UNASSIGNED";
        const isScheduled = elapsedTimeStatus === "SCHEDULED";
        if (!creationDate) {
          if (!isUnassigned && !isScheduled) return <div>-</div>;
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="cursor-help w-fit">
                  {isUnassigned ? "Sin asignar" : "Registrada - pendiente"}
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom" align="start">
                {isUnassigned
                  ? "La solicitud aún no ha sido asignada a un grupo."
                  : "El conteo iniciará en la próxima jornada hábil."}
              </TooltipContent>
            </Tooltip>
          );
        }

        const { formatted, colorClass } =
          getFormattedElapsedDaysWithColor(creationDate);

        const status = row.row?.original?.status?.toLowerCase();
        const shouldShowBadge =
          status === "can_extended" || status === "time_expired";
        const badgeColor = status === "can_extended" ? "warning" : "danger";

        return (
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Icon
                type="far"
                name="calendar"
                className={`ml-1 mr-4 ${colorClass}-quiet`}
                size="lg"
              />
              <span className={`${colorClass}`}>{formatted}</span>
            </div>
            {shouldShowBadge && (
              <Avatar size="xs" className="mr-2">
                <AvatarFallback
                  color={badgeColor}
                  className="flex items-center justify-center"
                >
                  <Icon
                    type="fas"
                    name="clock"
                    className={`text-${badgeColor}-quiet`}
                    size="sm"
                  />
                </AvatarFallback>
              </Avatar>
            )}
          </div>
        );
      },
    }),

    columnHelper.display({
      id: "actions",
      header: "",
      enableSorting: false,
      meta: {
        className: "w-[56px]",
      },
      cell: (row) => {
        return (
          <div className="w-full flex justify-end">
            <Menu
              entity="gestión"
              id={row.row?.original?.id?.toString() || "tramite"}
              groups={row.row?.original?.groups ?? []}
              groupsImLeaderOf={groupsImLeaderOf}
              onUpdateAssignee={(groupsProcessImLeaderOf: string[]) => {
                setGroupsIdToFind(groupsProcessImLeaderOf);
                onUpdateAssignee(row.row?.original);
                setModalState("userSelection");
              }}
            />
          </div>
        );
      },
    }),
  ].filter(({ id }) => {
    if (id === "assignedTo.name" && !shouldShowAssignedColumn) return false;
    return true;
  }) as ColumnDef<FormalityRequestType>[];
